import { useState, useCallback, useMemo, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../constants';
import { getToken } from '../../../services/tokenService';

export default function useFinanceData() {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterRecuStatus, setFilterRecuStatus] = useState('all');
  const [filterAcompteStatus, setFilterAcompteStatus] = useState('all');
  const [expandedIds, setExpandedIds] = useState(new Set());

  const getAuthHeader = useCallback(() => {
    const token = getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  const toggleExpand = useCallback((id) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const fetchData = useCallback(async (notify = false) => {
    try {
      setRefreshing(true);
      const res = await axios.get(`${API}/domium/subscribe/admin/all`, {
        headers: getAuthHeader(),
        params: { limit: 500 }
      });
      const data = res.data?.subscriptions || res.data || [];
      setDocuments(Array.isArray(data) ? data : []);
      if (notify) toast.success(`${data.length} souscription(s) chargee(s)`);
    } catch {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [getAuthHeader]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Helpers
  const isPaid = (doc) => {
    if (!doc) return false;
    const status = doc.paiement?.statut || '';
    return status === 'confirme' || status === 'paye' || doc.paiement_valide || doc.est_solde;
  };

  const getAmount = (doc) => {
    if (!doc) return 0;
    return doc.calcul?.contribution_ht || doc.calcul?.contribution_ttc || doc.paiement?.montant || 0;
  };

  const getMode = (doc) => doc?.paiement?.mode || doc?.mode_paiement || 'virement';

  const getName = (doc) => {
    if (!doc) return 'N/A';
    const id = doc.identite || {};
    return `${id.prenom || ''} ${id.nom || ''}`.trim() || id.raison_sociale || 'N/A';
  };

  const getContributionStatus = (doc) => {
    if (!doc) return 'pending';
    const totalAmount = getAmount(doc);
    const paidAmount = doc.paiements_recus?.reduce((sum, p) => sum + (p.montant || 0), 0) ||
                       (isPaid(doc) ? totalAmount : 0);
    if (paidAmount >= totalAmount && isPaid(doc)) return doc.recu_acquitte ? 'acquitted' : 'paid';
    if (paidAmount > 0) return 'partial';
    return 'pending';
  };

  const getEcheancePayments = (doc) => doc.paiements_echeances || [];

  const getAllPayments = (doc) => {
    const acomptes = doc.acomptes || [];
    const paiementsPartiels = doc.paiements_partiels || [];
    return [...acomptes, ...paiementsPartiels].sort((a, b) =>
      new Date(b.validated_at || b.date) - new Date(a.validated_at || a.date)
    );
  };

  const generateAcompteRecuReference = (doc, acompteIndex) => {
    const baseRef = doc.recu_contribution_ref || `RCS-${doc.reference}`;
    return `${baseRef}-AC${String(acompteIndex + 1).padStart(2, '0')}`;
  };

  const isEcheancePaid = (doc, echeanceIndex) => {
    return getEcheancePayments(doc).some(p => p.echeance_index === echeanceIndex);
  };

  const getEcheancePaidAmount = (doc, echeanceIndex) => {
    const payment = getEcheancePayments(doc).find(p => p.echeance_index === echeanceIndex);
    return payment?.montant || 0;
  };

  const generateRecuReference = (doc, echeanceIndex) => {
    const baseRef = doc.recu_contribution_ref || `RCS-${doc.reference}`;
    return `${baseRef}-ECH${String(echeanceIndex + 1).padStart(2, '0')}`;
  };

  // Stats
  const stats = useMemo(() => {
    const pending = documents.filter(d => !isPaid(d));
    const paid = documents.filter(d => isPaid(d));
    const acquitted = documents.filter(d => getContributionStatus(d) === 'acquitted');
    const partial = documents.filter(d => getContributionStatus(d) === 'partial');
    const totalAmount = documents.reduce((s, d) => s + getAmount(d), 0);
    const paidAmount = paid.reduce((s, d) => s + getAmount(d), 0);
    return {
      total: documents.length, pending: pending.length, paid: paid.length,
      acquitted: acquitted.length, partial: partial.length,
      totalAmount, paidAmount,
      rate: documents.length > 0 ? ((paid.length / documents.length) * 100).toFixed(1) : 0
    };
  }, [documents]);

  // Filtered
  const filtered = useMemo(() => {
    let result = [...documents];
    if (filterStatus === 'pending') result = result.filter(d => !isPaid(d));
    else if (filterStatus === 'paid') result = result.filter(d => isPaid(d));
    else if (filterStatus === 'partial') result = result.filter(d => getContributionStatus(d) === 'partial');
    if (filterRecuStatus === 'recu_pending') result = result.filter(d => getContributionStatus(d) === 'pending');
    else if (filterRecuStatus === 'recu_partial') result = result.filter(d => getContributionStatus(d) === 'partial');
    else if (filterRecuStatus === 'recu_paid') result = result.filter(d => getContributionStatus(d) === 'paid');
    else if (filterRecuStatus === 'recu_acquitted') result = result.filter(d => getContributionStatus(d) === 'acquitted');
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(d =>
        getName(d).toLowerCase().includes(term) ||
        (d.reference || '').toLowerCase().includes(term) ||
        (d.identite?.siret || '').includes(term) ||
        (d.recu_contribution_ref || '').toLowerCase().includes(term)
      );
    }
    return result;
  }, [documents, filterStatus, filterRecuStatus, searchTerm]);

  // Acomptes flat
  const getAllAcomptesFlat = useMemo(() => {
    const allAcomptes = [];
    documents.forEach(doc => {
      getAllPayments(doc).forEach((acompte, idx) => {
        allAcomptes.push({ ...acompte, docReference: doc.reference, docName: getName(doc), docAmount: getAmount(doc), acompteIndex: idx, doc });
      });
    });
    if (filterAcompteStatus === 'recu') return allAcomptes.filter(a => !a.is_scheduled && a.statut !== 'programme');
    if (filterAcompteStatus === 'programme') return allAcomptes.filter(a => a.is_scheduled || a.statut === 'programme');
    if (filterAcompteStatus === 'annule') return allAcomptes.filter(a => a.statut === 'annule');
    return allAcomptes;
  }, [documents, filterAcompteStatus]);

  // Acomptes stats
  const acomptesStats = useMemo(() => {
    const all = [];
    documents.forEach(doc => getAllPayments(doc).forEach(a => all.push(a)));
    const recus = all.filter(a => !a.is_scheduled && a.statut !== 'programme' && a.statut !== 'annule');
    const programmes = all.filter(a => a.is_scheduled || a.statut === 'programme');
    const annules = all.filter(a => a.statut === 'annule');
    return {
      total: all.length, recus: recus.length,
      montantRecu: recus.reduce((s, a) => s + (a.montant || 0), 0),
      programmes: programmes.length,
      montantProgramme: programmes.reduce((s, a) => s + (a.montant || 0), 0),
      annules: annules.length
    };
  }, [documents]);

  return {
    documents, loading, refreshing, searchTerm, setSearchTerm,
    filterStatus, setFilterStatus, filterRecuStatus, setFilterRecuStatus,
    filterAcompteStatus, setFilterAcompteStatus,
    expandedIds, toggleExpand, getAuthHeader, fetchData,
    isPaid, getAmount, getMode, getName, getContributionStatus,
    getEcheancePayments, getAllPayments, generateAcompteRecuReference,
    isEcheancePaid, getEcheancePaidAmount, generateRecuReference,
    stats, filtered, getAllAcomptesFlat, acomptesStats,
  };
}
